import startsWith from 'lodash/startsWith'

import countriesJson from './phone-input__countries.json'

type Country = {
  dial_code: string
  code: string
}
type Countries = Array<Country>

const countries: Countries = countriesJson

/**
 * Given a full phone number (joined with a country code and starting with a plus),
 * split it into the country code and the phone number itself
 *
 * NOTE: the provided phoneNumber argument should start with a plus (as our api returns it)
 */
export function splitPhoneNumber(phoneNumber: string) {
  const matchedCountry = countries.filter(country => {
    const countryCodeWithoutSpaces = country.dial_code.replace(/\s/g, '')
    return startsWith(phoneNumber, countryCodeWithoutSpaces)
  })[0]

  if (!matchedCountry) return

  const { dial_code: _dial_code, code } = matchedCountry
  const dial_code = _dial_code.replace(/\s/g, '')
  const phoneNumberProper = phoneNumber.substring(dial_code.length)

  return { phoneNumber: phoneNumberProper, code }
}

/**
 * Given a full phone number (joined with the country dial code and starting with a plus),
 * and the country 2-letter ISO code,
 * remove the dial code and return just the phone number itself
 *
 * NOTE: the provided phoneNumber argument should be in the format (our api returns it)
 * (i.e. start with a plus and have no spaces)
 */
export function separatePhoneNumberFromDialCode(
  phoneNumber: string,
  countryCode: string,
) {
  const matchedCountry = countries.filter(
    country => country.code.toLowerCase() === countryCode.toLowerCase(),
  )[0]

  if (!matchedCountry) return

  let { dial_code } = matchedCountry
  dial_code = dial_code.replace(/\s/g, '')
  const phoneNumberProper = phoneNumber.substring(dial_code.length)
  return phoneNumberProper
}

export const SERBIA_NATIONAL_PHONE_GROUPS = [2, 3, 2, 2] as const
export const SERBIA_NATIONAL_PHONE_PLACEHOLDER = '00 000 00 00'

export function stripPhoneDigits(value: string): string {
  return value.replace(/\D/g, '')
}

export function formatGroupedDigits(
  digits: string,
  groups: readonly number[],
): string {
  const maxDigits = groups.reduce((sum, size) => sum + size, 0)
  const normalized = stripPhoneDigits(digits).slice(0, maxDigits)
  const parts: string[] = []
  let offset = 0

  for (const size of groups) {
    if (offset >= normalized.length) break
    parts.push(normalized.slice(offset, offset + size))
    offset += size
  }

  return parts.join(' ')
}

export function buildFullPhoneNumber(
  countryCode: string,
  phone: string,
): string {
  return countryCode + stripPhoneDigits(phone)
}
